import React, { useEffect, useRef, useState } from "react";
import { stripePromise } from "./stripeClient";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import axios from "axios";
import {
  collection,
  addDoc,
  onSnapshot,
  serverTimestamp,
  where,
  query,
} from "firebase/firestore";
import {
  Clock,
  ShieldCheck,
  Mail,
  User,
  Phone,
  Tag,
  Search,
  ChevronDown,
  CreditCard,
  Lock,
  ArrowRight,
  Info,
  GraduationCap,
  Check,
  Landmark,
  Copy,
  CheckCircle2,
  X,
} from "lucide-react";

import { db } from "../../FirebaseConfig";
import { createUserAccount, paymentFailureMessage, payWithCard, matchCourse, normalizeCourseKey } from "./api";
import StatusModal from "./StatusModal";
import PaymentExpiryTimer from "./PaymentExpiryTimer";
import superLogo from "../logo/super.png";
import tritekLogo from "../logo/tritek-academy.png";
import "./PaymentForm.css";



const API_BASE = process.env.REACT_APP_API_BASE || "https://innovators.onrender.com";

// ─── Combined courses ─────────────────────────────────────────────────────────
// Every two-course pairing, one flat price. These get merged into the same
// courseOptions / courseDisplayMap the singles use, so pricing, product ids,
// the order summary and every payment branch keep working unchanged — only the
// picker knows the difference.

const COMBINED_PRICE = 399;

// Digital Marketing and every pairing that includes it are commented out
// rather than deleted, same as the single courses below, so the product ids
// are still to hand if it comes back.
/*
  WITHDRAWN WITH THE REST.

  Emptied rather than deleted. Note PMDA appears here as a pairing AND above as
  a programme of its own: this loop runs after that object, so re-enabling this
  list silently overwrites the single PMDA (productId 56409) with the combined
  one (76752). Re-enable one or the other, never both.
*/
const COMBINED_COURSES_AVAILABLE = false;

const COMBINED_COURSES = COMBINED_COURSES_AVAILABLE ? {
  // Project Management. PMBA stays a SINGLE course — Project Management with
  // Business Analysis is its own programme, so it is not a pairing of the two.
  PMCS:   { productId: 76751, display: "Project Management + Cyber security" },
  PMDA:   { productId: 76752, display: "Project Management + Data Analysis" },
  PMVA:   { productId: 76753, display: "Project Management + Virtual Assistant" },
  PMHA:   { productId: 76754, display: "Project Management + Health Analytics" },
  // PMDM:   { productId: 76755, display: "Project Management + Digital Marketing" },

  // Business Analysis
  BACS:   { productId: 76977, display: "Business Analysis + Cyber security" },
  BADA:   { productId: 76978, display: "Business Analysis + Data Analysis" },
  BAVA:   { productId: 76980, display: "Business Analysis + Virtual Assistant" },
  BAHA:   { productId: 76981, display: "Business Analysis + Health Analytics" },
  // BADM:   { productId: 76983, display: "Business Analysis + Digital Marketing" },

  // Cyber security
  CSDA:   { productId: 76756, display: "Cyber security + Data Analysis" },
  CSVA:   { productId: 76757, display: "Cyber security + Virtual Assistant" },
  CSHA:   { productId: 76758, display: "Cyber security + Health Analytics" },
  // CSDM:   { productId: 76759, display: "Cyber security + Digital Marketing" },

  // Data Analysis
  DAVA:   { productId: 76761, display: "Data Analysis + Virtual Assistant" },
  DAHA:   { productId: 76763, display: "Data Analysis + Health Analytics" },
  // DADM:   { productId: 76762, display: "Data Analysis + Digital Marketing" },

  // Virtual Assistant
  VAHA:   { productId: 76764, display: "Virtual Assistant + Health Analytics" },
  // VADM:   { productId: 76765, display: "Virtual Assistant + Digital Marketing" },

  // Health Analytics
  // HADM:   { productId: 76766, display: "Health Analytics + Digital Marketing" },
} : {};

// ─── Static bank details ──────────────────────────────────────────────────────

const BANK_DETAILS = {
  bankName: "HSBC",
  accountName: "Tritek Consulting Limited",
  accountNumber: "32338254",
  sortCode: "40-20-23",
};

// ─── Copy row (bank transfer modal) ───────────────────────────────────────────

const CopyRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="bank-detail-row">
      <div>
        <p className="bank-detail-label">{label}</p>
        <p className="bank-detail-value">{value}</p>
      </div>
      <button type="button" className={`bank-copy-btn${copied ? " copied" : ""}`} onClick={handleCopy} aria-label={`Copy ${label}`}>
        {copied ? <CheckCircle2 /> : <Copy />}
      </button>
    </div>
  );
};

// ─── Bank Transfer Modal ──────────────────────────────────────────────────────

const BankTransferModal = ({ open, onClose, courseName, amount }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">
          <X />
        </button>

        <div className="modal-header">
          <div className="modal-icon-wrap"><Landmark /></div>
          <h3 className="modal-title">Bank Transfer Details</h3>
        </div>

        <div className="bank-instruction-banner">
          Please transfer exactly <strong>£{amount}</strong> to the account below and use your{" "}
          <strong>full name</strong> as the payment reference so we can match your payment.
        </div>

        <div className="bank-detail-box">
          <CopyRow label="Bank" value={BANK_DETAILS.bankName} />
          <CopyRow label="Account Name" value={BANK_DETAILS.accountName} />
          <CopyRow label="Account Number" value={BANK_DETAILS.accountNumber} />
          <CopyRow label="Sort Code" value={BANK_DETAILS.sortCode} />
          <CopyRow label="Reference" value="Your Full Name" />
          <CopyRow label="Amount" value={`£${amount}`} />
        </div>

        <div className="bank-next-steps">
          <p className="bank-next-steps-title">What happens next?</p>
          <p className="bank-next-steps-text">
            Once we confirm receipt of your transfer, please send payment receipt to segun@tritekacademy.co.uk and your account will be created and you will
            receive login details by email. UK Faster Payments typically arrive within a few hours.
          </p>
        </div>

        <div className="bank-course-tag">Course: {courseName}</div>

        <button type="button" className="pay-btn" onClick={onClose}>
          <span>I've noted the details — close</span>
        </button>
      </div>
    </div>
  );
};

// ─── Redirect blocker modal ────────────────────────────────────────────────────

const RedirectModal = ({ open, methodLabel, courseName, amount }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <div className="redirect-modal-body">
          <div className="spinner" />
          <div>
            <p className="redirect-warning-title">Redirecting to {methodLabel}…</p>
            <p style={{ marginTop: 8, fontWeight: 700 }}>Do NOT close this tab. Do NOT press back.</p>
            <p style={{ marginTop: 4, color: "var(--text-secondary)", fontSize: "var(--size-sm)" }}>
              Wait until you return to the final confirmation page.
            </p>
          </div>
        </div>
        <div className="redirect-warning-box">
          <p className="redirect-warning-title">You are paying for:</p>
          <p className="redirect-warning-course">{courseName}</p>
          <p className="redirect-warning-amount">Amount: <strong>£{amount}</strong></p>
        </div>
      </div>
    </div>
  );
};

// ─── Main PaymentForm ─────────────────────────────────────────────────────────

const PaymentForm = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const pParam = urlParams.get("p");
  const customPrice = pParam ? parseFloat(pParam) : null;

  // Dropdown order is this object's order. Cybersecurity and Tech for Teens
  // sit at the bottom; everything commented out is kept in place rather than
  // deleted, so the product ids are still to hand if a course comes back.
  let courseOptions = {
    PMBA: { price: 399, productId: 51922, aliases: ["PM/BA", "PM-BA", "PM BA"] },
    PMDA: { price: 399, productId: 56409, aliases: ["PM/DA", "PM-DA", "PM DA"] },
    BADA: { price: 399, productId: 64347, aliases: ["BA/DA", "BA-DA", "BA DA"] },
    // ── Temporarily withdrawn ────────────────────────────────────────────────
    // Only PMBA, PMDA and BADA are on sale. Everything below is commented out
    // rather than deleted so the product ids and prices are still to hand when
    // it comes back. Restoring one is uncommenting its line.
    // PMHA: { price: 389, productId: 73158, aliases: ["PM/HA", "PM-HA", "PM HA"] },
    // DAHA: { price: 389, productId: 73159, aliases: ["DA/HA", "DA-HA", "DA HA"] },
    // BAHA: { price: 389, productId: 73155, aliases: ["BA/HA", "BA-HA", "BA HA"] },
    //PMBADA: { price: 459, productId: 69134, aliases: ["PMBADA"] },
    //"Business Analysis": { price: 459, productId: 72791, aliases: ["BA"] },
    //"Project Management": { price: 459, productId: 72790, aliases: ["PM"] },
    //'Data Analysis':        { price: 399, productId: 22767, aliases: ['Data Analytics'] },
    //'Digital Marketing':    { price: 249, productId: 45867 },
    //'Virtual Assistance':   { price: 399, productId: 52236, aliases: ['Virtual Assistant','va'] },
    //'UI/UX':                { price: 129, productId: 31278, aliases: ['UI-UX', 'UI UX', 'UX/UI'] },
    //'Product Management':   { price: 249, productId: 48478, aliases: ['Product Mgmt'] },
    //'Health Analyitcs':   { price: 399, productId: 69868, aliases: ['Health Analyitcs','ha'] },
    //'MoM':                  { price: 259, productId: 29201, aliases: ['MOM'] },
    //'AI for Professional':  { price: 99, productId: 52242, aliases: ['AI for Professionals', 'AI for Pros'] },
    //'Cybersecurity':        { price: 399, productId: 22766 },
    //'Tech for Teens': { price: 399, productId: 73397, aliases: ["tech-for-teens"] },
  };

  for (const [code, combo] of Object.entries(COMBINED_COURSES)) {
    courseOptions[code] = { price: COMBINED_PRICE, productId: combo.productId, combined: true };
  }

  /*
    Separators are ignored when matching a course.

    Links are written by people, and a combined course has an obvious name
    with no agreed punctuation: ?course=PMBACS, PMBA-CS, "PMBA + CS" and
    "pmba cs" are all plainly the same course, and a link that silently fell
    back to PMBA because of a dash was a quiet way to sell the wrong thing.
  */
  const normalize = normalizeCourseKey;
  const resolveCourseFromParam = (raw) => matchCourse(courseOptions, raw);

  /*
    ?p= WITH ?course= PRICES THAT COURSE. ?p= ALONE PRICES EVERY COURSE.

    ?p= alone used to be an accident: a link negotiated for one course
    re-priced the whole dropdown, so anybody who opened it and changed their
    mind bought a different course at the discounted rate. It was narrowed to
    ?course= only, and is now deliberately allowed again as a blanket promo
    link — every course at one price, which is what "?p= and nothing else"
    plainly reads as.

    Two things keep the old accident from coming back. The blanket form needs
    ?course= to be ABSENT, so a single-course link can never widen by
    mistake; and the catalogue is three courses at one price, so there is no
    cheaper course for a discount to leak onto.

    Anyone narrowing it again wants ?course= present: that path is unchanged.
  */
  const requestedCourse = resolveCourseFromParam(urlParams.get("course"));
  const validCustomPrice = customPrice && Number.isFinite(customPrice) && customPrice > 0;

  if (validCustomPrice) {
    courseOptions = requestedCourse
      ? { ...courseOptions, [requestedCourse]: { ...courseOptions[requestedCourse], price: customPrice } }
      : Object.fromEntries(
          Object.entries(courseOptions).map(([code, data]) => [code, { ...data, price: customPrice }])
        );
  }

  const courseDisplayMap = {
    PMBA: "Project Management / Business Analysis with AI",
    PMDA: "Project Management / Data Analysis",
    BADA: "Business Analysis / Data Analysis",
    PMHA: "Project Management / Healthcare Analysis",
    DAHA: "Data Analysis / Healthcare Analysis",
    BAHA: "Business Analysis / Healthcare Analysis",
    "Data Analysis": "Data Analysis with AI",
    "Tech for Teens": "Tech for Teens",
    // The keys are the stored product names and stay as they are — "Health
    // Analyitcs" included, typo and all, because it is what every existing
    // order records. Only what the candidate reads changes.
    "Virtual Assistance": "Virtual Assistant",
    "UI/UX": "UI/UX",
    "Product Management": "Product Management",
    "Health Analyitcs": "Health Analytics",
    "AI for Professional": "AI for Professionals",
    "Cybersecurity": "Cyber security with AI",
    ...Object.fromEntries(Object.entries(COMBINED_COURSES).map(([code, c]) => [code, c.display])),
  };

  const stripe = useStripe();
  const elements = useElements();

  const [formData, setFormData] = useState({ email: "", firstName: "", lastName: "", phone: "", course: "" });
  const [firstPaymentAmount, setFirstPaymentAmount] = useState(489);
  const [amountError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("klarna");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");
  const [acceptedConsent, setAcceptedConsent] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState("part");
  const [referralCode, setReferralCode] = useState("");
  const [staff, setStaff] = useState(null);

  // "single" | "combined" — which list the course picker is showing
  const [courseType, setCourseType] = useState("single");
  const [combinedQuery, setCombinedQuery] = useState("");

  // Redirect blocker (Klarna / Clearpay / PayPal)
  const [redirectOpen, setRedirectOpen] = useState(false);

  /*
    Apple Pay / Google Pay, through the Express Checkout Element.

    The Payment Request Button this replaced is deprecated, and it could not
    do the one thing needed here: show the wallets on a device that has not
    set them up. It also reported availability through canMakePayment(),
    which resolves { applePay, googlePay, link } — and link alone being true
    was enough to render a Quick Pay heading above an empty box.

    null until the element reports back; then the wallets it can actually show.
  */
  const [walletMethods, setWalletMethods] = useState(null);

  /*
    THE WALLET STRIP IS MOUNTED BY HAND, NOT THROUGH <ExpressCheckoutElement>.

    react-stripe-js creates the element inside a useLayoutEffect, which runs
    before the browser has laid the page out. On this page that produced an
    element that reported ready, sat at the right width, and drew nothing —
    an 8px empty iframe that never recovered. It survived the first load
    after a deploy and broke on every reload, because a warm cache lets the
    script win the race.

    elements.create() called from a plain useEffect runs after layout, which
    is the one thing that has been shown to render reliably in the browser
    where this fails. Same Stripe.js, same options — only the mount moment
    differs.
  */
  const walletBoxRef = useRef(null);
  const walletElementRef = useRef(null);
  const walletElementsRef = useRef(null);

  /*
    The handlers close over form state, so the element gets stable wrappers
    that read the current ones through a ref. Without this the sheet would
    confirm against whatever the course and amount were at mount.
  */
  const walletClickRef = useRef(null);
  const walletConfirmRef = useRef(null);
  const walletStripeRef = useRef(null);

  /*
    The wallet strip is mounted by hand rather than through
    <ExpressCheckoutElement>, and it keeps its own Elements group: the confirm
    path needs the group that owns the element, and the rest of the form's
    group carries a different amount and the card element.
  */
  useEffect(() => {
    if (!stripe || !walletBoxRef.current) return;

    let cancelled = false;

    const build = () => {
      const box = walletBoxRef.current;
      if (cancelled || !box) return;

      try { if (walletElementRef.current) walletElementRef.current.destroy(); } catch (e) { /* gone */ }

      const els = stripe.elements({
        mode: "payment",
        amount: Math.round((firstPaymentAmount || 489) * 100),
        currency: "gbp",
      });

      /*
        'always' shows Apple Pay and Google Pay on devices that have not set
        them up, instead of silently rendering nothing. It cannot force them
        onto platforms that do not support them at all.

        The others are off because this strip is labelled Apple Pay and Google
        Pay — Link, PayPal and Klarna have their own places in this form.
      */
      const ece = els.create("expressCheckout", {
        buttonHeight: 52,
        /*
          No layout option here on purpose. Setting
          { maxColumns: 2, maxRows: 2, overflow: "never" } stopped the element
          initialising altogether — ready never fired, so the whole Quick Pay
          section vanished rather than showing one button. If Google Pay needs
          coaxing out of the overflow menu, change one field at a time and
          check ready still fires.
        */
        paymentMethods: {
          applePay: "always",
          googlePay: "always",
          link: "never",
          paypal: "never",
          klarna: "never",
          amazonPay: "never",
        },
      });

      walletStripeRef.current = stripe;
      walletElementsRef.current = els;
      walletElementRef.current = ece;

      /*
        The availability object has every key set to false when the browser
        has no wallet at all, and an object is truthy — storing it as-is lit
        the Quick Pay heading and its divider around an empty box. Only the
        two wallets this strip is labelled for count.

        Stripe can revise availability after ready, so both events feed this.
      */
      const show = (avail) =>
        setWalletMethods(avail && (avail.applePay || avail.googlePay) ? avail : null);

      ece.on("ready", (e) => show(e.availablePaymentMethods));
      ece.on("availablepaymentmethodschange", (e) => show(e.availablePaymentMethods || e.paymentMethods));
      ece.on("click", (event) => walletClickRef.current && walletClickRef.current(event));
      ece.on("confirm", (event) => walletConfirmRef.current && walletConfirmRef.current(event));
      ece.mount(box);
    };

    // Settled page first, then a beat. Both matter: readyState complete on its
    // own was not late enough when the bundle came from a warm cache.
    build();

    return () => {
      cancelled = true;
      try { if (walletElementRef.current) walletElementRef.current.destroy(); } catch (e) { /* gone */ }
      walletElementRef.current = null;
      walletElementsRef.current = null;
      walletStripeRef.current = null;
    };
    // Built once per Stripe instance. firstPaymentAmount only seeds the group
    // here; the effect below keeps it in step, and rebuilding on every course
    // change would tear the buttons down mid-checkout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  // The wallet sheet quotes whatever this group holds, so the chosen course
  // has to reach it. Its own group means the form's Elements update no longer
  // covers it.
  useEffect(() => {
    if (!walletElementsRef.current || !firstPaymentAmount) return;
    walletElementsRef.current.update({ amount: Math.round(firstPaymentAmount * 100) });
  }, [firstPaymentAmount]);

  // Bank Transfer modal
  const [bankModalOpen, setBankModalOpen] = useState(false);

  const getCurrencySymbol = () => {
    switch (paymentMethod) {
      case "flexpay-us": return "$";
      case "flexpay-can": return "CAD";
      default: return "£";
    }
  };

  const singleCourseList = Object.entries(courseOptions).filter(([, d]) => !d.combined);
  const combinedCourseList = Object.entries(courseOptions).filter(([, d]) => d.combined);

  // Matches code and full title, so "health", "ha", "cyber" and "PMBADM" all
  // find something.
  const combinedMatches = (() => {
    const q = combinedQuery.trim().toLowerCase();
    if (!q) return combinedCourseList;
    return combinedCourseList.filter(([code]) =>
      code.toLowerCase().includes(q) || (courseDisplayMap[code] || "").toLowerCase().includes(q)
    );
  })();

  const selectCourse = (code) => {
    setFormData((prev) => ({ ...prev, course: code }));
    const data = courseOptions[code];
    if (data?.price != null) setFirstPaymentAmount(data.price);
  };

  const handleCourseTypeChange = (type) => {
    setCourseType(type);
    setCombinedQuery("");
    // Switching lists clears a selection that no longer belongs to it.
    const current = courseOptions[formData.course];
    const stillValid = type === "combined" ? current?.combined : current && !current.combined;
    if (!stillValid) {
      if (type === "single") selectCourse("PMBA");
      else setFormData((prev) => ({ ...prev, course: "" }));
    }
  };

  const selectedCourseKey = formData.course;
  const selectedCourseDisplay = courseDisplayMap[selectedCourseKey] || selectedCourseKey || "—";
  const selectedCoursePrice =
    courseOptions?.[selectedCourseKey]?.price ??
    (Number.isFinite(firstPaymentAmount) ? firstPaymentAmount : 0);

  const paymentMethodLabel =
    paymentMethod === "klarna" ? "Klarna"
    : paymentMethod === "clearpay" ? "Clearpay"
    : paymentMethod === "super" ? "Super"
    : paymentMethod === "paypal" ? "PayPal"
    : paymentMethod === "bank-transfer" ? "Bank Transfer"
    : "Credit Card";

  const payButtonLabel =
    paymentMethod === "klarna" ? "Pay With Klarna"
    : paymentMethod === "clearpay" ? "Pay With ClearPay"
    : paymentMethod === "super" ? "Pay Later With Super"
    : paymentMethod === "paypal" ? "Pay With PayPal"
    : paymentMethod === "bank-transfer" ? "View Bank Transfer Details"
    : "Proceed to Credit Card";

  const requiresRedirectWait =
    paymentMethod === "klarna" || paymentMethod === "clearpay" ||
    paymentMethod === "paypal" || paymentMethod === "super";

  const showCardElement =
    paymentMethod !== "klarna" && paymentMethod !== "clearpay" &&
    paymentMethod !== "paypal" && paymentMethod !== "bank-transfer" &&
    paymentMethod !== "super";

  const formIsValid =
    !!formData.email && !!formData.firstName && !!formData.lastName &&
    !!formData.phone && !!formData.course && acceptedConsent;

  // ── Staff ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get("email");
    if (!emailParam) { setStaff(null); return; }
    const staffQuery = query(collection(db, "influencers"), where("email", "==", emailParam));
    const unsubscribe = onSnapshot(staffQuery, (snapshot) => {
      if (!snapshot.empty) {
        const d = snapshot.docs[0].data();
        setStaff(d?.role === "user" ? d : null);
      } else { setStaff(null); }
    });
    return () => unsubscribe();
  }, []);

  // ── Course preselect ───────────────────────────────────────────────────────
  useEffect(() => {
    // Survives the Klarna/Clearpay/PayPal redirect so "Go to Home" returns to this exact URL
    sessionStorage.setItem("payReturnTo", window.location.pathname + window.location.search);
    const params = new URLSearchParams(window.location.search);
    const chosenCourse = resolveCourseFromParam(params.get("course")) ?? "PMBA";
    const courseData = courseOptions[chosenCourse];
    setFormData((prev) => ({ ...prev, course: chosenCourse }));
    setFirstPaymentAmount(courseData?.price ?? 489);
    if (courseData?.combined) setCourseType("combined");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Warn on tab close during redirect ─────────────────────────────────────
  useEffect(() => {
    if (!redirectOpen) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [redirectOpen]);

  // ── Referral code from URL ─────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref") || params.get("referral") || params.get("ref_code");
    if (ref) setReferralCode(ref.trim().toUpperCase());
  }, []);

  // ── Apple Pay / Google Pay ─────────────────────────────────────────────────
  /*
    THE AMOUNT THE WALLET SHEET QUOTES.

    Express Checkout needs the amount before the sheet opens, so Elements is
    created in deferred mode with a placeholder and the real figure is pushed
    in here whenever the course changes. Without this the sheet would quote
    whatever the provider was initialised with.
  */
  useEffect(() => {
    if (!elements || !firstPaymentAmount) return;
    elements.update({ amount: Math.round(firstPaymentAmount * 100) });
  }, [elements, firstPaymentAmount]);

  /*
    Everything that happens once a wallet payment succeeds. Identical to the
    card path: record the payment, create the account and the order, then say
    so. Kept separate from the confirm flow so the two concerns stay readable.
  */
  const completeWalletPayment = async ({ paymentIntentId, email, name, phone }) => {
    const courseData = courseOptions[formData.course];
    const amountMinor = Math.round(Number(firstPaymentAmount) * 100);

    await addDoc(collection(db, "payments"), {
      status: "succeeded", gateway: "stripe", method: "google-apple-pay", currency: "GBP",
      amountMinor, amountMajor: Number(firstPaymentAmount),
      paymentIntentId,
      customerEmail: email, customerName: name, customerPhone: phone || "",
      productName: formData.course, productId: courseData?.productId || null,
      meta: { source: "google-apple-pay" }, referralCode: referralCode || "",
      createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
    });

    const nameParts = (name || "").trim().split(" ");
    const accountResult = await createUserAccount(
      {
        email,
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        phone: phone || "",
      },
      {
        amountPaid: amountMinor,
        productId: courseData?.productId,
        currency: "GBP",
        referralCode: referralCode || "",
        courseKey: formData.course || "",
        source: "google-apple-pay",
        staff: staff || "",
      }
    );

    await addDoc(collection(db, "orders"), {
      name: name || "", email, phone: phone || "",
      staff: staff || "", paymentPlan: "full", paymentOption: "google-apple-pay",
      amountPaid: Number(firstPaymentAmount), productName: formData.course,
      productId: courseData?.productId, stripePaymentIntentId: paymentIntentId,
      status: accountResult.success ? "completed" : "failed",
      failureReason: accountResult.success ? "" : accountResult.error || "Account/order creation failed",
      createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
    });

    setSnackSeverity(accountResult.success ? "success" : "error");
    setSnackMessage(accountResult.success
      ? "Payment successful! Your account has been created."
      : "Payment succeeded but account creation failed. Please contact support.");
    setSnackOpen(true);
    sessionStorage.removeItem("paymentUserData");
  };

  /*
    The sheet is about to open. Refusing to resolve keeps it shut, which is
    how an incomplete form is blocked without the customer seeing a wallet
    sheet they cannot use.
  */
  const handleWalletClick = ({ resolve }) => {
    if (!formIsValid) {
      setSnackSeverity("error");
      setSnackMessage("Please complete the form and accept all consents before paying.");
      setSnackOpen(true);
      return;
    }
    resolve({ emailRequired: true, phoneNumberRequired: true });
  };

  const handleWalletConfirm = async (event) => {
    // The wallet strip has its own Stripe instance and Elements group, and
    // both submit() and confirmPayment() must be called on the pair that owns
    // the element — not on the form's.
    const walletElements = walletElementsRef.current;
    const walletStripe = walletStripeRef.current;
    if (!walletStripe || !walletElements) return;
    setLoading(true);
    try {
      const { error: submitError } = await walletElements.submit();
      if (submitError) throw submitError;

      const email = event?.billingDetails?.email || formData.email;
      const name = event?.billingDetails?.name || `${formData.firstName} ${formData.lastName}`.trim();
      const phone = event?.billingDetails?.phone || formData.phone || "";

      const createPI = await fetch(`${API_BASE}/stripe/create-intent`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: Math.round(firstPaymentAmount * 100), currency: "gbp", method: "card",
          email, name,
          metadata: { source: "google-apple-pay", course: formData.course || "", courseName: selectedCourseDisplay },
          returnUrl: `${window.location.origin}/confirmation`,
        }),
      });
      const piData = await createPI.json();
      if (!createPI.ok) throw new Error(piData.error || "Failed to create PaymentIntent");

      /*
        redirect: "if_required" keeps Apple Pay and Google Pay in the sheet.
        Without it Stripe would navigate away even though these wallets have
        nothing to redirect to.
      */
      const { error, paymentIntent } = await walletStripe.confirmPayment({
        elements: walletElements,
        clientSecret: piData.clientSecret,
        confirmParams: { return_url: `${window.location.origin}/confirmation` },
        redirect: "if_required",
      });
      if (error) throw error;

      await completeWalletPayment({ paymentIntentId: paymentIntent.id, email, name, phone });
    } catch (err) {
      console.error("[wallet] payment failed:", err);
      setSnackSeverity("error");
      setSnackMessage(paymentFailureMessage("flexpay-uk", err?.code || err?.decline_code, err?.message));
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };


  // ── Shared helpers ─────────────────────────────────────────────────────────

  const handleChange = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

  const validateBeforeSubmit = () => {
    if (!formData.course) { setSnackSeverity("error"); setSnackMessage("Please select a course."); setSnackOpen(true); return false; }
    if (!acceptedConsent) { setSnackSeverity("error"); setSnackMessage("Please accept the Terms and Conditions and digital content consent to continue."); setSnackOpen(true); return false; }
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.phone) {
      setSnackSeverity("error"); setSnackMessage("Please complete all required fields."); setSnackOpen(true); return false;
    }
    return true;
  };

  // Confirm-modal step removed — validated clicks go straight to payment,
  // or straight to the bank transfer details modal.
  const handlePayClick = () => {
    if (!validateBeforeSubmit()) return;
    if (paymentMethod === "bank-transfer") {
      setBankModalOpen(true);
      return;
    }
    handleFlexPaySubmit();
  };

  const handleFlexPaySubmit = async (event) => {
    event?.preventDefault?.();
    setLoading(true);
    setError("");

    if (!acceptedConsent) { setError("Please accept all consents before proceeding."); setLoading(false); return; }
    if (!stripe || !elements) { setLoading(false); return; }

    const { email, firstName, lastName, phone } = formData;
    const name = `${firstName} ${lastName}`;

    try {
      /** ── Super Payments (Pay Later / BNPL, Open Banking, Card) ── **/
      // Redirect to Super's hosted checkout. The transaction id is stashed in
      // sessionStorage rather than the return URL because Super only hands it
      // back in the create response, after the URLs have to be supplied.
      if (paymentMethod === "super") {
        const currency = "GBP";
        const amountMinor = Math.round(Number(firstPaymentAmount) * 100);
        const courseData = courseOptions[formData.course];
        const returnTo = `${window.location.origin}/confirmation`;

        const pendingPaymentRef = await addDoc(collection(db, "payments"), {
          status: "initiated", gateway: "super", method: "super", currency,
          amountMinor, amountMajor: Number(firstPaymentAmount),
          customerEmail: email, customerName: name, customerPhone: phone,
          productName: formData.course, productId: courseData?.productId || null,
          meta: { source: "ups" }, referralCode: referralCode || "",
          createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
        });

        const createRes = await fetch(`${API_BASE}/super/create-payment`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: amountMinor,
            externalReference: pendingPaymentRef.id,
            successUrl: `${returnTo}?super=success`,
            failureUrl: `${returnTo}?super=failed`,
            cancelUrl: `${returnTo}?super=cancelled`,
            metadata: { source: "ups", course: formData.course || "", email },
          }),
        });
        const superData = await createRes.json();
        if (!createRes.ok || !superData?.redirectUrl) {
          throw new Error(superData?.error || "Failed to start Super payment");
        }

        sessionStorage.setItem("paymentUserData", JSON.stringify({
          ...formData, firstPaymentAmount, productId: courseData?.productId,
          course: formData.course, staff: staff || "", paymentPlan,
          paymentMethod: "super", superTransactionId: superData.transactionId,
          paymentDocId: pendingPaymentRef.id, referralCode: referralCode || "",
        }));

        setRedirectOpen(true);
        window.location.assign(superData.redirectUrl);
        return;
      }

      /** ── PayPal ── **/
      if (paymentMethod === "paypal") {
        const currency = "GBP";
        const createPI = await fetch("https://innovators.onrender.com/stripe/create-intent", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: Math.round(Number(firstPaymentAmount) * 100), currency, method: "paypal", email, name,
            metadata: { source: "ups", course: formData.course || "", courseName: selectedCourseDisplay },
            returnUrl: `${window.location.origin}/confirmation`,
          }),
        });
        const piData = await createPI.json();
        if (!createPI.ok) throw new Error(piData.error || "Failed to create PaymentIntent");
        const { clientSecret, paymentIntentId } = piData;
        const courseData = courseOptions[formData.course];
        const pendingPaymentRef = await addDoc(collection(db, "payments"), {
          status: "initiated", gateway: "stripe", method: "paypal", currency,
          amountMinor: Math.round(Number(firstPaymentAmount) * 100), amountMajor: Number(firstPaymentAmount),
          paymentIntentId, customerEmail: email, customerName: name, customerPhone: phone,
          productName: formData.course, productId: courseData?.productId || null,
          meta: { source: "ups" }, referralCode: referralCode || "",
          createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
        });
        sessionStorage.setItem("paymentUserData", JSON.stringify({
          ...formData, firstPaymentAmount, productId: courseData?.productId,
          course: formData.course, staff: staff || "", paymentPlan: "part",
          paymentMethod: "paypal", paymentIntentId, paymentDocId: pendingPaymentRef.id, referralCode: referralCode || "",
        }));
        setRedirectOpen(true);
        const { error } = await stripe.confirmPayPalPayment(clientSecret, {
          return_url: `${window.location.origin}/confirmation`,
          payment_method: { billing_details: { email, name, phone } },
        });
        if (error) { setRedirectOpen(false); throw error; }
        setLoading(false); return;
      }

      /** ── Klarna / Clearpay ── **/
      if (paymentMethod === "klarna" || paymentMethod === "clearpay") {
        const currency = "GBP";
        const method = paymentMethod === "clearpay" ? "afterpay_clearpay" : "klarna";
        const createPI = await fetch("https://innovators.onrender.com/stripe/create-intent", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: Math.round(Number(firstPaymentAmount) * 100), currency, method, email, name,
            metadata: { source: "ups", course: formData.course || "", courseName: selectedCourseDisplay },
            returnUrl: `${window.location.origin}/confirmation`,
          }),
        });
        const piData = await createPI.json();
        if (!createPI.ok) throw new Error(piData.error || "Failed to create PaymentIntent");
        const { clientSecret, paymentIntentId } = piData;
        const courseData = courseOptions[formData.course];
        const pendingPaymentRef = await addDoc(collection(db, "payments"), {
          status: "initiated", gateway: "stripe", method: paymentMethod, currency,
          amountMinor: Math.round(Number(firstPaymentAmount) * 100), amountMajor: Number(firstPaymentAmount),
          paymentIntentId, customerEmail: email, customerName: name, customerPhone: phone,
          productName: formData.course, productId: courseData?.productId || null,
          meta: { source: "ups" }, referralCode: referralCode || "",
          createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
        });
        sessionStorage.setItem("paymentUserData", JSON.stringify({
          ...formData, firstPaymentAmount, productId: courseData?.productId,
          course: formData.course, staff: staff || "", paymentPlan: "part",
          paymentMethod, paymentIntentId, paymentDocId: pendingPaymentRef.id, referralCode: referralCode || "",
        }));
        setRedirectOpen(true);
        if (paymentMethod === "klarna") {
          const { error } = await stripe.confirmKlarnaPayment(clientSecret, {
            return_url: `${window.location.origin}/confirmation`,
            payment_method: { billing_details: { email, name, phone } },
          });
          if (error) { setRedirectOpen(false); throw error; }
        } else {
          const { error } = await stripe.confirmAfterpayClearpayPayment(clientSecret, {
            return_url: `${window.location.origin}/confirmation`,
            payment_method: { billing_details: { email, name, phone } },
          });
          if (error) { setRedirectOpen(false); throw error; }
        }
        setLoading(false); return;
      }

      /** ── Card / FlexPay ── **/
      const cardElement = elements.getElement(CardElement);
      const { error: pmErr, paymentMethod: stripePaymentMethod } = await stripe.createPaymentMethod({
        type: "card", card: cardElement, billing_details: { name, email },
      });
      if (pmErr) {
        setSnackSeverity("error"); setSnackMessage("Error creating payment method."); setSnackOpen(true);
        setLoading(false); return;
      }
      let endpoint = "";
      switch (paymentMethod) {
        case "flexpay-uk": endpoint = "https://innovators.onrender.com/create-customer"; break;
        case "flexpay-us": endpoint = "https://innovators.onrender.com/create-customer-usa"; break;
        case "flexpay-can": endpoint = "https://innovators.onrender.com/create-customer-us"; break;
        default:
          setSnackSeverity("error"); setSnackMessage("Invalid payment method"); setSnackOpen(true);
          setLoading(false); return;
      }
      const cardResult = await payWithCard({
        stripe, endpoint,
        payload: {
          name, email, phone,
          paymentMethodId: stripePaymentMethod.id,
          firstPaymentAmount,
          course: formData.course || "",
          courseName: selectedCourseDisplay,
          returnUrl: `${window.location.origin}/confirmation`,
        },
      });
      if (cardResult) {
        const courseData = courseOptions[formData.course];
        await addDoc(collection(db, "payments"), {
          status: "succeeded", gateway: "stripe", method: "card",
          currency: paymentMethod === "flexpay-us" ? "USD" : paymentMethod === "flexpay-can" ? "CAD" : "GBP",
          amountMinor: Math.round(Number(firstPaymentAmount) * 100), amountMajor: Number(firstPaymentAmount),
          paymentIntentId: cardResult?.firstPaymentIntent || null,
          stripeCustomerId: cardResult?.customerId || null,
          customerEmail: email, customerName: name, customerPhone: phone,
          productName: formData.course, productId: courseData?.productId || null,
          meta: { source: "flexpay" }, referralCode: referralCode || "",
          createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
        });
        const accountResult = await createUserAccount(
          { email, firstName, lastName, phone },
          { amountPaid: Math.round(Number(firstPaymentAmount) * 100), productId: courseData?.productId, currency: "GBP", referralCode: referralCode || "", courseKey: formData.course || "", source: paymentMethod, staff: staff || "" }
        );
        await addDoc(collection(db, "orders"), {
          name, email, phone, staff: staff || "", paymentPlan: "part",
          paymentOption: paymentMethod, amountPaid: Number(firstPaymentAmount),
          productName: formData.course, productId: courseData?.productId,
          createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
          status: accountResult.success ? "completed" : "failed",
          failureReason: accountResult.success ? "" : accountResult.error || "Account/order creation failed",
        });
        setSnackMessage(accountResult.success ? "Payment successful! Account created and access granted." : "Payment succeeded, but account creation failed.");
        setSnackSeverity(accountResult.success ? "success" : "error");
        setSnackOpen(true);
        sessionStorage.removeItem("paymentUserData");
      } else {
        setSnackSeverity("error"); setSnackMessage("Payment failed."); setSnackOpen(true);
      }
    } catch (err) {
      console.error("Payment error:", err);
      setSnackSeverity("error");
      setSnackMessage(paymentFailureMessage(paymentMethod, err?.code || err?.decline_code, err?.response?.data?.error || err?.message));
      setSnackOpen(true);
    } finally { setLoading(false); }
  };

  // Kept current every render so the mounted element never confirms against
  // a stale course or amount.
  walletClickRef.current = handleWalletClick;
  walletConfirmRef.current = handleWalletConfirm;

  const payBtnDisabled = loading || (paymentMethod === "flexpay-uk" && !!amountError);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ─── TOP HEADER BAR ──────────────────────────────────────────────── */}
      <header className="site-header">
        <div className="header-inner">
          {/*
            The supplied logo is white on transparency, and this header is
            white — so it sits on the brand colour rather than disappearing
            into the background. That purple was already here as the square
            behind the old lightning mark.
          */}
          <div className="brand">
            <img src={tritekLogo} alt="Tritek Academy" className="brand-logo" />
          </div>

          <div className="header-meta">
            <div className="timer-badge">
              <Clock size={13} />
              <span className="timer-label">Link expires in</span>
              <span className="timer-value"><PaymentExpiryTimer /></span>
            </div>
            <div className="secure-badge">
              <ShieldCheck size={14} />
              <span>Secured by SSL</span>
            </div>
          </div>
        </div>
      </header>

      <StatusModal open={snackOpen} onClose={() => setSnackOpen(false)} severity={snackSeverity} message={snackMessage} />

      <BankTransferModal
        open={bankModalOpen}
        onClose={() => setBankModalOpen(false)}
        courseName={selectedCourseDisplay}
        amount={selectedCoursePrice}
      />

      <RedirectModal
        open={redirectOpen}
        methodLabel={paymentMethodLabel}
        courseName={selectedCourseDisplay}
        amount={selectedCoursePrice}
      />

      {/* ─── MAIN LAYOUT ─────────────────────────────────────────────────── */}
      <main className="checkout-main">
        <div className="checkout-grid">

          {/* ══ LEFT COLUMN — FORM ══════════════════════════════════════════ */}
          <section className="form-column" aria-label="Customer details and payment">

            {/* Step 1: Customer Details */}
            <div className="card">
              <div className="card-header">
                <div className="step-badge">1</div>
                <div>
                  <h2 className="card-title">Your details</h2>
                  <p className="card-subtitle">Enter your contact information below</p>
                </div>
              </div>

              <div className="card-body">
                <div className="field-group">
                  <label className="field-label" htmlFor="email">
                    Email address <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <Mail className="input-icon" size={15} />
                    <input
                      className="field-input"
                      type="email"
                      id="email"
                      name="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange("email")}
                      disabled={loading}
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>

                <div className="field-row">
                  <div className="field-group">
                    <label className="field-label" htmlFor="first-name">
                      First name <span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <User className="input-icon" size={15} />
                      <input
                        className="field-input"
                        type="text"
                        id="first-name"
                        name="first_name"
                        value={formData.firstName}
                        onChange={handleChange("firstName")}
                        disabled={loading}
                        autoComplete="given-name"
                        required
                      />
                    </div>
                  </div>
                  <div className="field-group">
                    <label className="field-label" htmlFor="last-name">
                      Last name <span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <User className="input-icon" size={15} />
                      <input
                        className="field-input"
                        type="text"
                        id="last-name"
                        name="last_name"
                        value={formData.lastName}
                        onChange={handleChange("lastName")}
                        disabled={loading}
                        autoComplete="family-name"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="field-group">
                  <label className="field-label" htmlFor="phone">
                    Phone number <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <Phone className="input-icon" size={15} />
                    <input
                      className="field-input"
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="+44 7700 000000"
                      value={formData.phone}
                      onChange={handleChange("phone")}
                      disabled={loading}
                      autoComplete="tel"
                      required
                    />
                  </div>
                </div>

                {/* <div className="field-group">
                  <label className="field-label" htmlFor="referral">
                    Referral code <span className="optional-tag">Optional</span>
                  </label>
                  <div className="input-wrapper">
                    <Tag className="input-icon" size={15} />
                    <input
                      className="field-input"
                      type="text"
                      id="referral"
                      name="referral_code"
                      placeholder="If someone referred you, enter their code"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      disabled={loading}
                    />
                  </div>
                  <p className="field-hint">If someone referred you, enter their code here.</p>
                </div> */}
              </div>
            </div>

            {/* Step 2: Course Selection */}
            <div className="card">
              <div className="card-header">
                <div className="step-badge">2</div>
                <div>
                  <h2 className="card-title">Select your course</h2>
                  <p className="card-subtitle">Choose the programme you wish to enroll in</p>
                </div>
              </div>

              <div className="card-body">
                {/* Single course, or any two combined at one flat price. The
                    choice disappears entirely while the pairings are withdrawn —
                    a toggle leading to an empty list is worse than no toggle. */}
                {combinedCourseList.length > 0 && (
                <div className="plan-toggle-group plan-toggle-inline" role="group" aria-label="Course type">
                  <label className="plan-option" htmlFor="course-single">
                    <input
                      className="plan-radio"
                      type="radio"
                      id="course-single"
                      name="course_type"
                      value="single"
                      checked={courseType === "single"}
                      onChange={() => handleCourseTypeChange("single")}
                      disabled={loading}
                    />
                    <span className="plan-card">
                      <span className="plan-card-title">Single course</span>
                      <span className="plan-card-desc">One programme</span>
                    </span>
                  </label>
                  <label className="plan-option" htmlFor="course-combined">
                    <input
                      className="plan-radio"
                      type="radio"
                      id="course-combined"
                      name="course_type"
                      value="combined"
                      checked={courseType === "combined"}
                      onChange={() => handleCourseTypeChange("combined")}
                      disabled={loading}
                    />
                    <span className="plan-card">
                      <span className="plan-card-title">Combined courses</span>
                      <span className="plan-card-desc">
                        Any two &mdash; {getCurrencySymbol()}{COMBINED_PRICE}
                      </span>
                    </span>
                  </label>
                </div>
                )}

                {courseType === "single" ? (
                  <div className="field-group" style={{ marginTop: 20 }}>
                    <label className="field-label">
                      Please Select Your Course <span className="required">*</span>
                    </label>
                    <ul className="combined-list" role="listbox" aria-label="Single courses">
                      {singleCourseList.map(([course, data]) => {
                        const isSelected = formData.course === course;
                        return (
                          <li key={course} role="option" aria-selected={isSelected}>
                            <button
                              type="button"
                              className={`combined-option${isSelected ? " is-selected" : ""}`}
                              onClick={() => selectCourse(course)}
                              disabled={loading}
                            >
                              <span className="combined-option-name">
                                {courseDisplayMap[course] || course}
                              </span>
                              <span className="combined-option-price">
                                {getCurrencySymbol()}{data.price}
                              </span>
                              {isSelected && <Check size={14} className="combined-option-tick" />}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <div className="field-group" style={{ marginTop: 20 }}>
                    <label className="field-label" htmlFor="combined-search">
                    Find your combined course <span className="required">*</span>
                    </label>
                    <div className="select-wrapper">
                      <Search className="input-icon" size={15} />
                      <input
                        className="field-select"
                        id="combined-search"
                        type="search"
                        placeholder="Search e.g. health, cyber, data"
                        value={combinedQuery}
                        onChange={(e) => setCombinedQuery(e.target.value)}
                        disabled={loading}
                        autoComplete="off"
                      />
                    </div>

                    <ul className="combined-list" role="listbox" aria-label="Combined courses">
                      {combinedMatches.map(([code, data]) => {
                        const isSelected = formData.course === code;
                        return (
                          <li key={code} role="option" aria-selected={isSelected}>
                            <button
                              type="button"
                              className={`combined-option${isSelected ? " is-selected" : ""}`}
                              onClick={() => selectCourse(code)}
                              disabled={loading}
                            >
                              <span className="combined-option-name">
                                {courseDisplayMap[code] || code}
                              </span>
                              <span className="combined-option-price">
                                {getCurrencySymbol()}{data.price}
                              </span>
                              {isSelected && <Check size={14} className="combined-option-tick" />}
                            </button>
                          </li>
                        );
                      })}
                      {combinedMatches.length === 0 && (
                        <li className="combined-empty">
                          Nothing matches &ldquo;{combinedQuery}&rdquo;.
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Step 3: Terms & Conditions */}
            <div className="card">
              <div className="card-header">
                <div className="step-badge">3</div>
                <div>
                  <h2 className="card-title">Terms &amp; conditions</h2>
                  <p className="card-subtitle">Please review and accept before proceeding</p>
                </div>
              </div>

              <div className="card-body">
                <label className="checkbox-label" htmlFor="terms-check">
                  <input
                    className="checkbox-input"
                    type="checkbox"
                    id="terms-check"
                    name="consent"
                    checked={acceptedConsent}
                    onChange={(e) => setAcceptedConsent(e.target.checked)}
                    disabled={loading}
                    required
                  />
                  <span className="checkbox-custom">
                    <Check className="check-icon" />
                  </span>
                  <span className="checkbox-text">
                    I agree to the{" "}
                    <a href="https://triteklibrary.co.uk/terms-and-conditions" className="inline-link" target="_blank" rel="noopener noreferrer">
                      Terms and Conditions
                    </a>{" "}
                    and expressly request immediate access to the digital content after purchase. I understand
                    that once access begins, I forfeit my 14-day cancellation right under the{" "}
                    <strong>Consumer Rights Act 2015</strong> and the{" "}
                    <strong>Consumer Contracts Regulations 2013</strong>. <span className="required">(Required)</span>
                  </span>
                </label>
              </div>
            </div>

            {/* Step 4: Payment Method */}
            <div className="card">
              <div className="card-header">
                <div className="step-badge">4</div>
                <div>
                  <h2 className="card-title">Payment</h2>
                  <p className="card-subtitle">Choose how you'd like to pay</p>
                </div>
              </div>

              <div className="card-body">

                {/*
                  NO WALLET IS A STATE, NOT A BLANK.

                  Apple will not let the button render without an active card
                  in Wallet — canMakePayment resolves null and Stripe refuses
                  to mount it, which is Apple's rule and the reason a button
                  cannot simply be forced on. Rendering nothing at all though
                  made "unavailable" and "broken" look identical, on a device
                  where the console is not reachable without a Mac and a
                  cable.
                */}
                {/*
                  The heading and divider only appear once the element has told
                  us it has something to show. The old code rendered them on a
                  truthy canMakePayment() and then left an empty box underneath
                  when the only thing available was Link.
                */}
                {walletMethods && (
                  <div className="section-divider-label">
                    <span>Quick Pay — Full payment</span>
                  </div>
                )}

                {/*
                  Hidden with height:0 rather than display:none, because the
                  box has to keep a real width for Stripe to measure when the
                  element finally mounts into it.
                */}
                <div
                  ref={walletBoxRef}
                  className="stripe-payment-request-wrapper"
                  style={walletMethods ? undefined : { height: 0, overflow: "hidden" }}
                />

                {walletMethods && (
                  <div className="or-divider">
                    <span>Or pay another way</span>
                  </div>
                )}

                {/* Payment plan toggle */}
                <div className="plan-toggle-group" role="group" aria-label="Payment plan">
                  <label className="plan-option" htmlFor="plan-part">
                    <input
                      className="plan-radio"
                      type="radio"
                      id="plan-part"
                      name="payment_plan"
                      value="part"
                      checked={paymentPlan === "part"}
                      onChange={(e) => { setPaymentPlan(e.target.value); setPaymentMethod("klarna"); }}
                      disabled={loading}
                    />
                    <span className="plan-card">
                      <span className="plan-card-title">Part payment</span>
                      <span className="plan-card-desc">Pay in instalments</span>
                    </span>
                  </label>
                  <label className="plan-option" htmlFor="plan-full">
                    <input
                      className="plan-radio"
                      type="radio"
                      id="plan-full"
                      name="payment_plan"
                      value="full"
                      checked={paymentPlan === "full"}
                      onChange={(e) => { setPaymentPlan(e.target.value); setPaymentMethod("flexpay-uk"); }}
                      disabled={loading}
                    />
                    <span className="plan-card">
                      <span className="plan-card-title">Full payment</span>
                      <span className="plan-card-desc">Pay the total amount now</span>
                    </span>
                  </label>
                </div>

                {/* Stripe card element — shown for FlexPay card payments only */}
                {showCardElement && (
                  <div className="field-group" style={{ marginTop: 20 }}>
                    <label className="field-label">
                      Card details <span className="required">*</span>
                    </label>
                    <div className="card-element-wrapper">
                      <CardElement
                        options={{
                          style: {
                            base: { fontSize: "16px", color: "#0f0f10", "::placeholder": { color: "#9ea3af" } },
                            invalid: { color: "#dc2626" },
                          },
                        }}
                      />
                    </div>
                    <p className="card-element-hint">Card details are not stored</p>
                  </div>
                )}

                {/* Payment method dropdown */}
                <div className="field-group" style={{ marginTop: 20 }}>
                  <label className="field-label" htmlFor="payment-method">
                    Payment Method <span className="required">*</span>
                  </label>
                  <div className="select-wrapper">
                    <CreditCard className="input-icon" size={15} />
                    <select
                      className="field-select"
                      id="payment-method"
                      name="payment_method"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      disabled={loading}
                      required
                    >
                      {paymentPlan === "part" ? (
                        <>
                          <option value="klarna">Klarna</option>
                          <option value="clearpay">ClearPay</option>
                          {/* Hidden until SUPER_SECRET_KEY + SUPER_PAYMENT_INITIATOR_ID are set
                              on the server — without them every Super payment errors.
                              <option value="super">Super — Pay Later</option> */}
                        </>
                      ) : (
                        <>
                          <option value="flexpay-uk">Card payment (GBP)</option>
                          <option value="bank-transfer">Bank Transfer</option>
                        </>
                      )}
                    </select>
                    <ChevronDown className="select-chevron" size={15} />
                  </div>
                </div>

                {/* Bank transfer hint */}
                {paymentMethod === "bank-transfer" && (
                  <p className="redirect-notice notice-info">
                    <Info size={14} />
                    <span>
                      Click the button below to view our bank details. Transfer the exact amount and use your
                      full name as the reference.
                    </span>
                  </p>
                )}

                {error && <p className="error-text">{error}</p>}

                {/* CTA Button */}
                <button type="button" className="pay-btn" id="btn-pay" onClick={handlePayClick} disabled={payBtnDisabled}>
                  {loading ? (
                    <div className="spinner spinner-sm" />
                  ) : (
                    <>
                      <Lock className="pay-btn-icon" />
                      <span>{payButtonLabel}</span>
                      <ArrowRight className="pay-btn-arrow" />
                    </>
                  )}
                </button>

                {requiresRedirectWait && (
                  <p className="redirect-notice">
                    <Info size={14} />
                    <span>
                      After confirming, you will be redirected. Do <strong>not</strong> close this page.
                    </span>
                  </p>
                )}

              </div>
            </div>

          </section>

          {/* ══ RIGHT COLUMN — ORDER SUMMARY ════════════════════════════════ */}
          <aside className="summary-column" aria-label="Order summary">

            <div className="summary-card sticky-summary">

              <div className="summary-header">
                <span className="summary-eyebrow">Order summary</span>
                <div className="trust-pill">
                  <ShieldCheck size={12} />
                  <span>Secure checkout</span>
                </div>
              </div>

              <div className="summary-course-block">
                <div className="course-icon-wrap">
                  <GraduationCap size={20} />
                </div>
                <div className="course-info">
                  <p className="course-label">You are enrolling in</p>
                  <p className="course-name">{selectedCourseDisplay}</p>
                  <p className="course-provider">Tritek Training</p>
                </div>
              </div>

              <div className="summary-divider" />

              <div className="price-breakdown">
                <div className="price-row">
                  <span className="price-label">Course fee</span>
                  <span className="price-value">{getCurrencySymbol()}{Number(selectedCoursePrice).toFixed(2)}</span>
                </div>
                <div className="price-row">
                  <span className="price-label">Discount</span>
                  <span className="price-value discount-value">— {getCurrencySymbol()}0.00</span>
                </div>
              </div>

              <div className="summary-divider" />

              <div className="price-total-row">
                <span className="price-total-label">Total due today</span>
                <span className="price-total-value">{getCurrencySymbol()}{Number(selectedCoursePrice).toFixed(2)}</span>
              </div>

              <div className="summary-method-block">
                <div className="method-indicator">
                  <CreditCard className="method-icon" />
                  <div>
                    <p className="method-label">Paying via</p>
                    <p className="method-name">{paymentMethodLabel}</p>
                  </div>
                </div>
              </div>

              <div className="trust-row">
                <div className="trust-item"><Lock size={13} /><span>256-bit SSL</span></div>
                <div className="trust-item"><ShieldCheck size={13} /><span>Encrypted</span></div>
                <div className="trust-item"><Check size={13} /><span>PCI DSS</span></div>
              </div>

              <div className="accepted-methods-section">
                <p className="accepted-label">Accepted payment methods</p>
                <div className="card-logos">
                  <span className="method-badge visa-badge">VISA</span>
                  <span className="method-badge mc-badge">MC</span>
                  <span className="method-badge amex-badge">AMEX</span>
                  <span className="method-badge gpay-badge">GPay</span>
                  <span className="method-badge applepay-badge"> Pay</span>
                  <span className="method-badge klarna-badge">klarna</span>
                  <span className="method-badge clearpay-badge">Clearpay</span>
                  <img className="card-logo super-logo" src={superLogo} alt="Super" />
                </div>
              </div>

            </div>

          </aside>

        </div>
      </main>

      {/* ─── FOOTER ───────────────────────────────────────────────────────── */}
      <footer className="site-footer">
        <div className="footer-inner">
          <p className="footer-text">© 2026 Tritek Training. All rights reserved.</p>
          <div className="footer-links">
            <a href="https://triteklibrary.co.uk/privacy-policy" className="footer-link">Privacy Policy</a>
            <a href="https://triteklibrary.co.uk/terms-and-conditions" className="footer-link">Terms of Service</a>
            <a href="#" className="footer-link">Contact Support</a>
          </div>
        </div>
      </footer>
    </>
  );
};

/*
  Express Checkout needs the amount when Elements is created, so the provider
  opens in deferred mode with a placeholder; PaymentForm pushes the real
  course price in with elements.update() as soon as a course is chosen.
*/
const StripeWrapper = () => (
  <Elements
    stripe={stripePromise}
    options={{ mode: "payment", amount: 39900, currency: "gbp", paymentMethodCreation: "manual" }}
  >
    <PaymentForm />
  </Elements>
);

export default StripeWrapper;